import React, { useState, useCallback, useEffect, useMemo } from "react";
import { FaTimes } from "react-icons/fa";
import { toast } from "react-hot-toast";
import userService from "../../api/services/userService";
import uploadService from "../../api/services/uploadService";
import {
  PHONE_DIAL_CODES,
  buildFullContactNo,
  clampNationalDigitsForDial,
  getNationalLengthLimits,
  maxBirthDateIsoForAge18,
  minBirthDateIso120Years,
} from "../../data/phoneDialCodes";

const inputClass =
  "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500";
const labelClass = "block text-xs font-semibold text-gray-600 mb-1";

function defaultAdultBirthDate() {
  return maxBirthDateIsoForAge18();
}

const initialForm = {
  email: "",
  firstName: "",
  lastName: "",
  gender: "",
  birthDate: defaultAdultBirthDate(),
};

function apiErrorMessage(err, fallback) {
  if (!err || typeof err !== "object") return fallback;
  return err.message || fallback;
}

export default function CreateAppUserModal({ isOpen, onClose, onCreated }) {
  const [form, setForm] = useState(initialForm);
  const [phoneDial, setPhoneDial] = useState("+92");
  const [phoneNational, setPhoneNational] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const birthMax = useMemo(() => maxBirthDateIsoForAge18(), []);
  const birthMin = useMemo(() => minBirthDateIso120Years(), []);
  const phoneLimits = useMemo(() => getNationalLengthLimits(phoneDial), [phoneDial]);

  useEffect(() => {
    setPhoneNational((prev) => clampNationalDigitsForDial(phoneDial, prev));
  }, [phoneDial]);

  const reset = useCallback(() => {
    setForm({ ...initialForm, birthDate: defaultAdultBirthDate() });
    setPhoneDial("+92");
    setPhoneNational("");
    setFile(null);
    setPreviewUrl((u) => {
      if (u) URL.revokeObjectURL(u);
      return null;
    });
    setResult(null);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return f && f.type.startsWith("image/") ? URL.createObjectURL(f) : null;
    });
    setFile(f || null);
  };

  const copyPassword = () => {
    const pw = result?.temporaryPassword;
    if (!pw || !navigator.clipboard) return;
    navigator.clipboard.writeText(pw).then(
      () => toast.success("Password copied"),
      () => toast.error("Could not copy")
    );
  };

  const handleDone = () => {
    handleClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const email = form.email.trim();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const national = phoneNational.replace(/\D/g, "");
    const contactNo = buildFullContactNo(phoneDial, national);
    const birthDate = form.birthDate;

    if (!email || !firstName || !lastName || !national || !birthDate) {
      toast.error("Fill email, first name, last name, country code + phone, and birth date.");
      return;
    }
    if (!form.gender) {
      toast.error("Please select a gender.");
      return;
    }
    if (national.length < phoneLimits.min || national.length > phoneLimits.max) {
      const range =
        phoneLimits.min === phoneLimits.max
          ? `exactly ${phoneLimits.min} digits`
          : `${phoneLimits.min}–${phoneLimits.max} digits`;
      toast.error(`Enter a valid phone number (${range} after the country code).`);
      return;
    }
    if (birthDate > birthMax) {
      toast.error("Birth date must be at least 18 years ago.");
      return;
    }
    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please choose a profile photo (image file).");
      return;
    }

    setSubmitting(true);
    try {
      const up = await uploadService.single(file);
      const imageUrl = up?.imageUrl || null;
      if (!imageUrl) {
        toast.error(up?.message || "Image upload failed");
        setSubmitting(false);
        return;
      }

      const res = await userService.createAppUser({
        email,
        firstName,
        lastName,
        contactNo,
        gender: form.gender,
        birthDate,
        imageUrl,
      });

      if (res?.responseCode === 201 || res?.success) {
        if (res?.profileImageAttachFailed) {
          toast.error(
            res?.message ||
              "User was created but the profile image could not be attached. Add a photo from Edit user."
          );
        } else {
          toast.success(res?.message || "User created");
        }
        setResult(res);
        onCreated?.();
      } else {
        toast.error(res?.message || "Create failed");
      }
    } catch (err) {
      const code = err?.responseCode;
      const msg = apiErrorMessage(err, "Failed to create user");
      if (code === 409 && /phone|number|contact/i.test(msg)) {
        toast.error("This phone number is already registered. Please use another number.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50">
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-user-title">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gray-50">
          <h2 id="create-user-title" className="text-lg font-bold text-gray-900">
            Create app user
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            aria-label="Close">
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {result ? (
          <div className="px-5 py-5 space-y-4 overflow-y-auto">
            <p className="text-sm text-gray-700">
              User <strong>#{result.user?.id}</strong> was created. Share the temporary password below with them (it is
              not shown again).
            </p>
            <div>
              <span className={labelClass}>Temporary password</span>
              <div className="flex gap-2 items-stretch">
                <code className="flex-1 rounded-lg bg-gray-100 border border-gray-200 px-3 py-2 text-xs break-all">
                  {result.temporaryPassword}
                </code>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="shrink-0 px-3 py-2 rounded-lg text-sm font-semibold bg-gray-200 hover:bg-gray-300 text-gray-800">
                  Copy
                </button>
              </div>
            </div>
            {result.user?.imageUrl ? (
              <div>
                <span className={labelClass}>Profile image</span>
                <img
                  src={result.user.imageUrl}
                  alt=""
                  className="h-20 w-20 rounded-full object-cover border border-gray-200"
                />
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleDone}
              className="w-full py-2.5 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-5 py-4 space-y-4">
            <p className="text-xs text-gray-500">
              Creates a customer account with a random password (shown after success). No email or SMS verification. A
              profile photo is required (uploaded via the admin image endpoint).
            </p>

            <div>
              <label className={labelClass}>Profile photo (required)</label>
              <input type="file" accept="image/*" onChange={handleFile} className="text-sm text-gray-700 w-full" />
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt=""
                  className="mt-2 h-24 w-24 rounded-full object-cover border-2 border-gray-200"
                />
              ) : null}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelClass}>Email</label>
                <input
                  name="email"
                  type="email"
                  autoComplete="off"
                  value={form.email}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>First name</label>
                <input name="firstName" value={form.firstName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Last name</label>
                <input name="lastName" value={form.lastName} onChange={handleChange} className={inputClass} required />
              </div>
              <div>
                <label className={labelClass}>Gender (required)</label>
                <select name="gender" value={form.gender} onChange={handleChange} className={inputClass} required>
                  <option value="">Select gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Contact (country code + number)</label>
                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                  <select
                    value={phoneDial}
                    onChange={(e) => setPhoneDial(e.target.value)}
                    className={`${inputClass} sm:max-w-[200px] shrink-0`}>
                    {PHONE_DIAL_CODES.map((pc) => (
                      <option key={pc.code} value={pc.code}>
                        {pc.code} ({pc.country})
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    placeholder="Phone number (digits only)"
                    value={phoneNational}
                    maxLength={phoneLimits.max}
                    onChange={(e) => setPhoneNational(clampNationalDigitsForDial(phoneDial, e.target.value))}
                    className={`${inputClass} flex-1 min-w-0`}
                    required
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  {phoneLimits.min === phoneLimits.max
                    ? `Exactly ${phoneLimits.max} digits after the code.`
                    : `${phoneLimits.min}–${phoneLimits.max} digits after the code.`}
                </p>
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Birth date (must be 18+)</label>
                <input
                  name="birthDate"
                  type="date"
                  min={birthMin}
                  max={birthMax}
                  value={form.birthDate}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Calendar opens within allowed range; default is the latest date for age 18.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2 pb-2 border-t border-gray-100">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200">
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50">
                {submitting ? "Creating…" : "Create user"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
